import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

import { useApp } from '../../context/AppContext';
import KPICard from '../../components/KPICard/KPICard';
import Card from '../../components/Card/Card';
import { StabilityBadge } from '../../components/Badge/Badge';
import OutlierList from '../../components/OutlierList/OutlierList';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/* ── helpers ── */

/** Filter fleet by operator + status from AppContext */
function FF(D, operator, status) {
  return (D.fleet || []).filter(r => {
    const opMatch = !operator || operator === 'All' || r.O === operator;
    const stMatch = !status || status === 'All' || r.S === status;
    return opMatch && stMatch;
  });
}

function Ffl(D, operator) {
  return (D.flights || []).filter(r =>
    !operator || operator === 'All' || r.operator === operator
  );
}

export default function LifecycleRisk() {
  const { operator, status, D } = useApp();
  const [searchQuery, setSearchQuery] = useState('');

  /* ── utilisation per registration ── */
  const { stable, mod, unstable, byReg, outliers } = useMemo(() => {
    const byReg = {};
    D.util.forEach(r => {
      if (!byReg[r.reg]) byReg[r.reg] = { hrs: 0, n: 0 };
      byReg[r.reg].hrs += r.hrs || 0;
      byReg[r.reg].n++;
    });

    const avgHrsList = Object.values(byReg).map(v => v.hrs / v.n);
    const stable   = avgHrsList.filter(h => h >= 8).length;
    const mod      = avgHrsList.filter(h => h >= 5 && h < 8).length;
    const unstable = avgHrsList.filter(h => h < 5).length;

    const total  = avgHrsList.length || 1;
    const fleetAvg = avgHrsList.reduce((s, h) => s + h, 0) / total;

    /* build outlier list — regs that deviate most from fleet avg */
    const outliers = Object.entries(byReg)
      .map(([reg, v]) => {
        const avgHrs = v.hrs / v.n;
        const delta  = avgHrs - fleetAvg;
        const fleetRow = D.fleet.find(f => f.R === reg) || {};
        return { R: reg, avgHrs, delta, V: fleetRow.V || '—', O: fleetRow.O || '—' };
      })
      .filter(r => Math.abs(r.delta) > 1.5)
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 8);

    return { stable, mod, unstable, byReg, outliers };
  }, [D]);

  /* ── lifecycle distribution ── */
  const { early, mature, late, ep, mp, lp } = useMemo(() => {
    const f    = FF(D, operator, status);
    const early  = f.filter(r => r.Age && r.Age < 5).length;
    const mature = f.filter(r => r.Age && r.Age >= 5 && r.Age < 15).length;
    const late   = f.filter(r => r.Age && r.Age >= 15).length;
    const tot    = f.filter(r => r.Age).length || 1;
    const ep = Math.round((early  / tot) * 100);
    const mp = Math.round((mature / tot) * 100);
    const lp = Math.round((late   / tot) * 100);
    return { early, mature, late, ep, mp, lp };
  }, [D, operator, status]);

  /* ── route concentration ── */
  const { sorted: routesSorted, top1, top5, top10, risk, riskCol } = useMemo(() => {
    const routes = {};
    Ffl(D, operator).forEach(r => {
      const k = `${r.oico}→${r.dico}`;
      routes[k] = (routes[k] || 0) + 1;
    });
    const sorted = Object.entries(routes).sort((a, b) => b[1] - a[1]);
    const tot  = Ffl(D, operator).length || 1;
    const top1  = sorted[0] ? Math.round((sorted[0][1] / tot) * 100) : 0;
    const top5  = Math.round(sorted.slice(0, 5).reduce((s, e) => s + e[1], 0) / tot * 100);
    const top10 = Math.round(sorted.slice(0, 10).reduce((s, e) => s + e[1], 0) / tot * 100);
    const risk   = top1 < 5 ? 'Low' : top1 < 15 ? 'Medium' : 'High';
    const riskCol = { Low: '#16a34a', Medium: '#d97706', High: '#dc2626' }[risk];
    return { sorted, top1, top5, top10, risk, riskCol };
  }, [D, operator]);

  /* ── doughnut data ── */
  const doughnutData = {
    labels: ['High Util (≥8h/day)', 'Medium (5–8h/day)', 'Low (<5h/day)'],
    datasets: [{
      data: [stable, mod, unstable],
      backgroundColor: ['#16a34a', '#d97706', '#dc2626'],
      borderWidth: 0,
    }],
  };
  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { font: { size: 11 }, padding: 14, usePointStyle: true, pointStyleWidth: 8 },
      },
      tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed}` } },
    },
  };

  /* ── performance detail table ── */
  const tableRows = useMemo(() => {
    const filtered = FF(D, operator, status);
    const q = searchQuery.toLowerCase();
    return filtered
      .map(r => {
        const utilData = byReg[r.R] || { hrs: 0, n: 0 };
        const avgHrs   = utilData.n ? utilData.hrs / utilData.n : 0;

        /* total hours & cycles from util records */
        const utilRecs = D.util.filter(u => u.reg === r.R);
        const totalHrs    = utilRecs.reduce((s, u) => s + (u.hrs || 0), 0);
        const totalCycles = utilRecs.reduce((s, u) => s + (u.cyc || u.cycles || 0), 0);

        const hrsPerCycle = totalCycles ? (totalHrs / totalCycles).toFixed(2) : '—';
        const maxUtil = 14; // sensible daily max for progress bar scaling
        const pct     = Math.min(100, Math.round((avgHrs / maxUtil) * 100));
        const barCol  = avgHrs >= 8 ? '#16a34a' : avgHrs >= 5 ? '#d97706' : '#dc2626';

        /* intensity: totalHrs / (age * 365 * 24) as percentage of possible hours */
        const intensityPct = r.Age
          ? Math.min(100, Math.round((totalHrs / (r.Age * 365 * 24)) * 100))
          : 0;

        return { ...r, avgHrs, totalHrs: Math.round(totalHrs), totalCycles: Math.round(totalCycles), hrsPerCycle, pct, barCol, intensityPct };
      })
      .filter(r => {
        if (!q) return true;
        return (
          (r.R  || '').toLowerCase().includes(q) ||
          (r.O  || '').toLowerCase().includes(q) ||
          (r.V  || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => b.avgHrs - a.avgHrs);
  }, [D, operator, status, searchQuery, byReg]);

  return (
    <div className="page-content">
      {/* KPI row */}
      <div className="krow krow-3">
        <KPICard label="Stable Aircraft"  value={stable}   accent="kg" sub="≥8h/day" />
        <KPICard label="Moderate Risk"    value={mod}      accent="ka" sub="5–8h/day" />
        <KPICard label="High Risk"        value={unstable} accent="kr" sub="<5h — action required" alert={unstable > 0} />
      </div>

      {/* Main grid */}
      <div className="g3b">
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Lifecycle Stage Distribution */}
          <Card title="Lifecycle Stage Distribution" icon="🔬" iconClass="ip">
            <div className="lc-bar">
              <div className="lc-seg" style={{ flex: ep || 1, background: '#1246d6' }}>
                {ep > 8 ? `${ep}%` : ''}
              </div>
              <div className="lc-seg" style={{ flex: mp || 1, background: '#16a34a' }}>
                {mp > 8 ? `${mp}%` : ''}
              </div>
              <div className="lc-seg" style={{ flex: lp || 1, background: '#d97706' }}>
                {lp > 8 ? `${lp}%` : ''}
              </div>
            </div>
            <div className="lc-legend">
              <div className="lc-li">
                <div className="lc-dot" style={{ background: '#1246d6' }} />
                Early (&lt;5 yrs) — {early}
              </div>
              <div className="lc-li">
                <div className="lc-dot" style={{ background: '#16a34a' }} />
                Mature (5–15 yrs) — {mature}
              </div>
              <div className="lc-li">
                <div className="lc-dot" style={{ background: '#d97706' }} />
                Late (&gt;15 yrs) — {late}
              </div>
            </div>
          </Card>

          {/* Route Concentration Risk */}
          <Card title="Route Concentration Risk" icon="🛡️" iconClass="ig">
            <div className="risk-val" style={{ color: riskCol }}>{risk}</div>
            <div className="risk-lbl" style={{ color: riskCol }}>Concentration Risk Level</div>
            <div className="risk-row"><span>Top Route Share</span><span className="risk-v">{top1}%</span></div>
            <div className="risk-row"><span>Top 5 Routes Share</span><span className="risk-v">{top5}%</span></div>
            <div className="risk-row"><span>Top 10 Routes Share</span><span className="risk-v">{top10}%</span></div>
            <div className="risk-row"><span>Total Routes</span><span className="risk-v">{routesSorted.length}</span></div>
            <div className="risk-row">
              <span>Busiest Route</span>
              <span className="risk-v">{routesSorted[0] ? routesSorted[0][0] : '—'}</span>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Aircraft Stability Distribution */}
          <Card title="Aircraft Stability Distribution" icon="📈" iconClass="ip">
            <div className="cw">
              <Doughnut data={doughnutData} options={doughnutOpts} />
            </div>
          </Card>

          {/* Utilisation Outliers */}
          <Card
            title="Utilisation Outliers"
            icon="⚠️"
            iconClass="ir"
            subtitle="Aircraft deviating from fleet average"
            noPad
          >
            <OutlierList items={outliers} />
          </Card>
        </div>
      </div>

      {/* Performance Detail table */}
      <Card
        title="Performance Detail"
        subtitle="All aircraft with utilisation metrics"
        icon="📋"
        iconClass="ib"
        noPad
        headerRight={
          <div className="sw">
            <span className="si">🔍</span>
            <input
              className="sinp"
              placeholder="Search aircraft…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        }
      >
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>Reg</th>
                <th>Operator</th>
                <th>Variant</th>
                <th>Total Hours</th>
                <th>Total Cycles</th>
                <th>Daily Util</th>
                <th>Intensity %</th>
                <th>Age</th>
                <th>Hrs/Cycle</th>
                <th>Stability</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 14px' }}>
                    No aircraft match the current filters.
                  </td>
                </tr>
              ) : (
                tableRows.map((r, i) => (
                  <tr key={`${r.R}-${i}`}>
                    <td><strong>{r.R || '—'}</strong></td>
                    <td>{r.O || '—'}</td>
                    <td>{r.V || '—'}</td>
                    <td>{r.totalHrs.toLocaleString()}</td>
                    <td>{r.totalCycles.toLocaleString()}</td>
                    <td style={{ minWidth: 120 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx)', marginBottom: 3 }}>
                        {r.avgHrs.toFixed(1)}h/day
                      </div>
                      <div className="pb">
                        <div className="pbf" style={{ width: `${r.pct}%`, background: r.barCol }} />
                      </div>
                    </td>
                    <td>{r.intensityPct}%</td>
                    <td>{r.Age ? `${r.Age} yrs` : '—'}</td>
                    <td>{r.hrsPerCycle}</td>
                    <td><StabilityBadge avgHrs={r.avgHrs} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
